import { useState } from "react";
import { motion } from "framer-motion";
import { Bot, Send, Sparkles } from "lucide-react";
import { useAITutorStore } from "@/stores/ai-tutor-store";
import { cn } from "@/lib/cn";

export type AITutorMiniPromptProps = {
  /**
   * Chapter to pin the chat to. Normally the user's most-recent chapter
   * (from useResume), so the AI has context. If null, the prompt opens
   * the sheet without a chapter -- the sheet then shows its "pick a
   * chapter" CTA.
   */
  pinnedChapterId: string | null;
  /** Optional first name -- used in the placeholder text ~50% of the time. */
  firstName?: string;
};

const SUGGESTED_PROMPTS = [
  "Explain the toughest concept I've covered",
  "Quiz me on what I learned this week",
  "Summarise where I'm weakest",
  "Give me a five-minute drill",
];

/**
 * Small "Ask the tutor" composer on the dashboard. Mirrors the AI Tutor
 * sheet's bottom input visually so the affordance is recognisable, but
 * lives in the page flow. On submit it stashes the prompt + pinned
 * chapter in the AI tutor store and opens the sheet -- the sheet's
 * composer then pre-fills from the stashed prompt.
 */
export function AITutorMiniPrompt({ pinnedChapterId, firstName }: AITutorMiniPromptProps) {
  const openTutor = useAITutorStore((s) => s.open);
  const [text, setText] = useState("");

  const placeholder = firstName && firstName !== "you" && Math.random() < 0.5
    ? `Ask anything, ${firstName}…`
    : "Ask anything — the tutor's listening…";

  const submit = (prompt?: string) => {
    const value = (prompt ?? text).trim();
    if (!value) return;
    openTutor({ prompt: value, chapterId: pinnedChapterId });
    setText("");
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
    >
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-7 w-7 rounded-md bg-brand-soft text-brand">
            <Bot className="h-3.5 w-3.5" />
          </span>
          <h3 className="font-display text-sm font-semibold tracking-tight text-text-muted uppercase tracking-[0.14em]">
            AI Tutor
          </h3>
        </div>
        <span className="flex items-center gap-1 text-[11px] text-text-subtle">
          <Sparkles className="h-3 w-3" />
          context-aware
        </span>
      </header>

      <div
        className={cn(
          "rounded-2xl border border-border bg-surface p-3 sm:p-4",
          "focus-within:border-brand/40 focus-within:shadow-[0_0_0_3px_rgb(249_115_22_/_0.08)]",
          "transition-[border-color,box-shadow] duration-150",
        )}
      >
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          className="flex items-end gap-2"
        >
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            className={cn(
              "flex-1 bg-transparent text-[15px] text-text placeholder:text-text-subtle",
              "px-2 py-2 focus:outline-none",
            )}
          />
          <button
            type="submit"
            disabled={text.trim().length === 0}
            aria-label="Send to tutor"
            className={cn(
              "h-9 w-9 grid place-items-center rounded-lg flex-shrink-0",
              "bg-brand text-white shadow-sm",
              "hover:scale-[1.04] active:scale-[0.96] transition-transform",
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
            )}
          >
            <Send className="h-4 w-4 -ml-0.5" />
          </button>
        </form>

        {/* Suggested prompts -- horizontal scroll on mobile. */}
        <div
          className={cn(
            "mt-3 flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5",
            "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          )}
        >
          {SUGGESTED_PROMPTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => submit(p)}
              className={cn(
                "flex-shrink-0 px-2.5 py-1 rounded-full text-xs",
                "border border-border bg-surface-2 text-text-muted",
                "hover:bg-surface-3 hover:text-text transition-colors",
              )}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </motion.section>
  );
}
